import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import {
  FeatureName,
  SELECTABLE_FEATURES,
  getFeatureDisplayName,
} from "../../../constants/features";
import { useEmployeeTerminology } from "../../../utils/terminology";

interface ManageFeaturesDialogProps {
  open: boolean;
  onClose: () => void;
  user: any;
  onSave: (features: string[]) => Promise<void>;
  availableFeatures?: string[]; // Features available based on company's subscription
}

const ManageFeaturesDialog: React.FC<ManageFeaturesDialogProps> = ({
  open,
  onClose,
  user,
  onSave,
  availableFeatures,
}) => {
  const { employees } = useEmployeeTerminology();
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  // Use available features from company subscription, or default to all selectable features
  const displayFeatures = availableFeatures || SELECTABLE_FEATURES;

  // Initialize selected features when dialog opens
  useEffect(() => {
    if (open && user) {
      // Start with user's allowed features or all display features
      let features = user.allowedFeatures || displayFeatures;

      // Filter to only include features that are available to the company
      features = features.filter((f: string) => displayFeatures.includes(f));

      setSelectedFeatures(features);
      setSelectAll(features.length === displayFeatures.length);
    }
  }, [open, user, displayFeatures]);

  const handleFeatureToggle = (feature: string) => {
    setSelectedFeatures((prev) => {
      if (prev.includes(feature)) {
        const newFeatures = prev.filter((f) => f !== feature);
        setSelectAll(false);
        return newFeatures;
      } else {
        const newFeatures = [...prev, feature];
        setSelectAll(newFeatures.length === displayFeatures.length);
        return newFeatures;
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFeatures([]);
      setSelectAll(false);
    } else {
      setSelectedFeatures(displayFeatures);
      setSelectAll(true);
    }
  };

  const getLabel = () => {
    return "";
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(selectedFeatures);
      onClose();
    } catch (error) {
      console.error("Failed to save features:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Manage Feature Access
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select which features {user?.email} can access. By default, members
          have access to all features.
        </Typography>

        <FormControlLabel
          control={<Checkbox checked={selectAll} onChange={handleSelectAll} />}
          label={<strong>Select All</strong>}
          sx={{ mb: 1 }}
        />

        <FormGroup>
          {displayFeatures.map((feature) => (
            <FormControlLabel
              key={feature}
              control={
                <Checkbox
                  checked={selectedFeatures.includes(feature)}
                  onChange={() => handleFeatureToggle(feature)}
                />
              }
              label={getFeatureDisplayName(feature as FeatureName, employees)}
            />
          ))}
        </FormGroup>

        <Box
          sx={{
            mt: 2,
            p: 2,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <strong>Note:</strong> Owner and Admin roles always have access to
            all features. Feature restrictions only apply to Member role.
            {availableFeatures && (
              <>
                {" "}
                Only features available in your company's subscription tier are
                shown.
              </>
            )}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={loading}
        >
          Save Features
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ManageFeaturesDialog;
